import React, { useState } from 'react'
import { toast } from 'react-toastify'

import { Alert, Button, Dialog } from '@clientbase/clientbase-library'
import { Box, Stack, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { ChargedCreditCard } from 'models/Invoice'
import { RecurrenceCardParams } from 'models/RecurrenceCard'
import moment from 'moment'

import IconContainer from 'components/IconContainer/IconContainer'

import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import * as S from '../../ModalRecurrenceDetail.styles'

interface CreditCardDetailsDialogProps {
  isOpen: boolean
  onClose: () => void
  recurrenceCard?: RecurrenceCardParams | null
  chargedCreditCard?: ChargedCreditCard | null
  recurrenceUuid?: string
  onSuccess?: () => void
}

const CreditCardDetailsDialog: React.FC<CreditCardDetailsDialogProps> = ({
  isOpen,
  onClose,
  recurrenceCard,
  chargedCreditCard,
  recurrenceUuid,
  onSuccess
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false)

  const handleUnlinkCard = async () => {
    try {
      setLoading(true)
      const id = toast.loading('Desvinculando cartão de crédito...')

      const response = await DataService({
        type: 'DELETE',
        url: `/v1/recurrences/${recurrenceUuid}/card`
      })

      const errorMessage =
        response.error && (response.detail_error || response.message)

      toast.update(id, {
        render: response.error
          ? errorMessage
          : 'Cartão de crédito desvinculado com sucesso!',
        type: response.error ? 'error' : 'success',
        ...toastProps
      })

      if (!response.error) {
        setShowConfirmDialog(false)
        onClose()
        if (onSuccess) {
          onSuccess()
        }
      }
    } catch (error) {
      toast.error('Erro ao desvincular o cartão de crédito.')
    } finally {
      setLoading(false)
    }
  }

  const isRecurrenceContext = !!recurrenceCard && !!recurrenceUuid
  const creditCard = recurrenceCard?.creditCard ?? chargedCreditCard

  const showExpiration = isRecurrenceContext
  const showCreatedAt = isRecurrenceContext || !!chargedCreditCard?.isSaved

  const cardCreatedAt = isRecurrenceContext
    ? recurrenceCard?.createdAt
    : chargedCreditCard?.createdAt

  if (!creditCard) {
    return null
  }

  return (
    <>
      <Dialog
        PaperProps={{
          sx: {
            maxWidth: '550px'
          }
        }}
        title=""
        setIsOpenDialog={onClose}
        isOpenDialog={isOpen}
      >
        <Stack alignItems="center" gap={2} mt="-45px">
          <IconContainer icon="creditCardChecked" />
          <Typography variant="mdBold">Detalhes do Cartão</Typography>

          {recurrenceCard?.creditCard?.requiresReregistration && (
            <Alert severity="warning">
              Recadastro necessário: este cartão foi cadastrado na adquirente
              anterior e não é mais cobrado. O cliente recebe o pedido de
              cadastro no vencimento; ao cadastrar o cartão pela fatura, a
              recorrência volta a ser cobrada automaticamente.
            </Alert>
          )}

          <Box width="100%">
            <Stack gap={1.5}>
              <Box display="flex" gap={1.25} alignItems="end">
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  Número do cartão
                </Typography>
                <S.Separator sx={{ marginTop: '4px' }} />
                <Typography variant="smBold">
                  {creditCard.first4Digits} **** **** {creditCard.last4Digits}
                </Typography>
              </Box>

              <Box display="flex" gap={1.25} alignItems="end">
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  Nome do titular
                </Typography>
                <S.Separator sx={{ marginTop: '4px' }} />
                <Typography variant="smBold">
                  {creditCard.holderName || '-'}
                </Typography>
              </Box>

              {showExpiration && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[400]}
                  >
                    Validade
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography variant="smBold">
                    {creditCard.expirationMonth?.toString().padStart(2, '0')}/
                    {creditCard.expirationYear}
                  </Typography>
                </Box>
              )}

              {creditCard.cardBrand && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[400]}
                  >
                    Bandeira
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography variant="smBold">
                    {creditCard.cardBrand}
                  </Typography>
                </Box>
              )}

              {showCreatedAt && cardCreatedAt && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[400]}
                  >
                    Data do cadastro
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography variant="smBold">
                    {moment(cardCreatedAt, 'YYYY-MM-DDTHH:mm:ss')
                      .format('DD/MM/YYYY HH:mm:ss')
                      .toString()}
                  </Typography>
                </Box>
              )}

              {isRecurrenceContext && (
                <Box display="flex" gap={1.25} alignItems="end">
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[400]}
                  >
                    Status
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography
                    variant="smBold"
                    sx={{
                      color:
                        recurrenceCard?.status === 'active'
                          ? theme.palette.success.main
                          : theme.palette.neutral[400]
                    }}
                  >
                    {recurrenceCard?.status === 'active' ? 'Ativo' : 'Inativo'}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Stack
            direction="row"
            mt={2}
            gap={1}
            width="100%"
            justifyContent="center"
          >
            <Button
              onClick={onClose}
              fullWidth
              variantButton="neutral"
              type="button"
            >
              Fechar
            </Button>
            {isRecurrenceContext && (
              <Button
                onClick={() => setShowConfirmDialog(true)}
                fullWidth
                variantButton="action"
                type="button"
                sx={{ whiteSpace: 'nowrap', px: 3 }}
              >
                Desvincular cartão
              </Button>
            )}
          </Stack>
        </Stack>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        PaperProps={{
          sx: {
            maxWidth: '400px'
          }
        }}
        title=""
        setIsOpenDialog={() => setShowConfirmDialog(false)}
        isOpenDialog={showConfirmDialog}
      >
        <Stack alignItems="center" gap={2} mt="-45px">
          <IconContainer
            icon="creditCardChecked"
            color={theme.palette.error.main}
          />
          <Typography variant="mdBold" textAlign="center">
            Confirmar desvinculação
          </Typography>

          <Typography
            variant="smLight"
            textAlign="center"
            color={theme.palette.neutral[500]}
          >
            Tem certeza que deseja desvincular este cartão de crédito da
            recorrência?
          </Typography>

          <Typography
            variant="smLight"
            textAlign="center"
            color={theme.palette.error.main}
          >
            Após a desvinculação, as cobranças futuras com cartão recorrente não
            serão processadas automaticamente até que um novo cartão seja
            vinculado.
          </Typography>

          <Stack
            direction="row"
            mt={2}
            gap={1}
            width="100%"
            justifyContent="center"
          >
            <Button
              onClick={() => setShowConfirmDialog(false)}
              disabled={loading}
              fullWidth
              variantButton="neutral"
              type="button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUnlinkCard}
              disabled={loading}
              loading={loading}
              fullWidth
              variantButton="primary"
              type="button"
            >
              Desvincular
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </>
  )
}

export default CreditCardDetailsDialog
